// src/pages/Plaatsingen.js
// vPL-016
//
// StaffIQ HRM - Plaatsingen
// Opzet gelijk aan Aanvragen / Vacatures:
// - Links/boven: zoeken + knoppen
// - Midden: overzicht met sortering en kolomfilters
// - Rechts: detailkaart

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const VERSION = "vPL-016";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const raw = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return 0;

  let cleaned = raw;

  if (cleaned.includes(",")) {
    // Nederlandse invoer: 2,35 of 2.350,75
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // Engelse/database-notatie: 2.35 moet 2.35 blijven.
    // Alleen meerdere punten zien we als duizendtallen: 2.350.750 -> 2350750
    const dotCount = (cleaned.match(/\./g) || []).length;
    if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function decimalInput(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";

  const n = parseDecimal(v);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function cleanDecimalInputValue(v) {
  return String(v || "").replace(/[^0-9,.-]/g, "");
}

function formatDecimalInputValue(v, decimals = 2) {
  const raw = String(v || "").trim();
  if (!raw) return "";
  const n = parseDecimal(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function boolToJaNee(v) {
  return v === true ? "j" : "n";
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function emptyFormToday() {
  return {
    id: null,
    placement_date: new Date().toISOString().slice(0, 10),
    placement_number: "",
    status: "Concept",
    application_id: "",
    vacancy_id: "",
    organisation_id: "",
    contact_id: "",
    personnel_id: "",
    job_title: "",
    start_date: "",
    end_date: "",
    hours_per_week: "",
    schedule_text: "",
    location: "",
    place: "",
    region: "",
    cao: "",
    wage_period: "week",
    invoice_period: "week",
    factor: "",
    hourly_wage: "",
    sales_rate: "",
    travel_costs: "",
    allowances: "",
    invoice_agreement: "",
    candidate_status: "Voorgesteld",
    customer_status: "In behandeling",
    customer_confirmed: false,
    candidate_confirmed: false,
    internal_note: "",
    external_note: "",
    remarks: "",
    active: true,
  };
}

function pickOrgName(o) {
  return safeTrim(o?.search_name || o?.zoeknaam || o?.name || o?.naam || o?.bedrijfsnaam || "");
}

function orgIdOf(o) {
  return o?.id ?? o?.organisation_id ?? o?.organisatie_id ?? "";
}

function orgNameFromList(list, id, fallback = "") {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return pickOrgName(found) || fallback || "";
}

function pickContactName(c) {
  const fn = safeTrim(c?.first_name || c?.voornaam || "");
  const ln = safeTrim(c?.last_name || c?.achternaam || "");
  const full = safeTrim(c?.full_name || c?.naam || "");
  return safeTrim(c?.search_name || c?.display_name || (ln && fn ? `${ln}, ${fn}` : "") || full || fn || "");
}

function contactIdOf(c) {
  return c?.id ?? c?.contact_id ?? "";
}

function personnelName(p) {
  const first = safeTrim(p?.first_name || "");
  const prefix = safeTrim(p?.name_prefix || "");
  const last = safeTrim(p?.last_name || "");
  return safeTrim(p?.search_name || [last, [first, prefix].filter(Boolean).join(" ")].filter(Boolean).join(", ") || [first, prefix, last].filter(Boolean).join(" "));
}

function normalizePlacement(raw = {}) {
  return {
    ...raw,
    id: raw.id ?? null,
    placement_date: isoDate(raw.placement_date),
    start_date: isoDate(raw.start_date),
    end_date: isoDate(raw.end_date),
    application_id: raw.application_id ?? "",
    vacancy_id: raw.vacancy_id ?? "",
    organisation_id: raw.organisation_id ?? "",
    contact_id: raw.contact_id ?? "",
    personnel_id: raw.personnel_id ?? "",
    hours_per_week: decimalInput(raw.hours_per_week, 2),
    factor: decimalInput(raw.factor),
    hourly_wage: decimalInput(raw.hourly_wage),
    sales_rate: decimalInput(raw.sales_rate),
    travel_costs: decimalInput(raw.travel_costs),
    customer_confirmed: raw.customer_confirmed === true,
    candidate_confirmed: raw.candidate_confirmed === true,
    active: parseActiveValue(raw) !== false,
  };
}


function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 9 }}>
      <label style={{ display: "block", fontSize: 12, color: "#555", marginBottom: 3 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

export default function Plaatsingen() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [applications, setApplications] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [invoiceSettings, setInvoiceSettings] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyFormToday());
  const [listIndex, setListIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");
  const [q, setQ] = useState(getParam("q"));

  const EMPTY_VAL = "__EMPTY__";
  const [dateFilter, setDateFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [personFilter, setPersonFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [caoFilter, setCaoFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");

  const [mode, setMode] = useState("search");
  const isFormLocked = isReadonlyRole || mode !== "form";
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const rightScrollRef = useRef(null);

  const dateRef = useRef(null);
  const numberRef = useRef(null);
  const applicationRef = useRef(null);
  const vacancyRef = useRef(null);
  const relationRef = useRef(null);
  const contactRef = useRef(null);
  const personnelRef = useRef(null);
  const functionRef = useRef(null);
  const statusRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const hoursRef = useRef(null);
  const scheduleRef = useRef(null);
  const locationRef = useRef(null);
  const placeRef = useRef(null);
  const regionRef = useRef(null);
  const caoRef = useRef(null);
  const wagePeriodRef = useRef(null);
  const invoicePeriodRef = useRef(null);
  const factorRef = useRef(null);
  const wageRef = useRef(null);
  const salesRateRef = useRef(null);
  const travelCostsRef = useRef(null);
  const allowancesRef = useRef(null);
  const invoiceAgreementRef = useRef(null);
  const candidateStatusRef = useRef(null);
  const customerStatusRef = useRef(null);
  const internalNoteRef = useRef(null);
  const externalNoteRef = useRef(null);

  const focusOrderRefs = [
    dateRef,
    numberRef,
    applicationRef,
    vacancyRef,
    relationRef,
    contactRef,
    personnelRef,
    functionRef,
    statusRef,
    startDateRef,
    endDateRef,
    hoursRef,
    scheduleRef,
    locationRef,
    placeRef,
    regionRef,
    caoRef,
    wagePeriodRef,
    invoicePeriodRef,
    factorRef,
    wageRef,
    salesRateRef,
    travelCostsRef,
    allowancesRef,
    invoiceAgreementRef,
    candidateStatusRef,
    customerStatusRef,
    internalNoteRef,
    externalNoteRef,
  ];

  async function loadOrganisations() {
    try {
      const data = await tryFetchJson(["/api/organisations"]);
      setOrganisations(Array.isArray(data) ? data : []);
    } catch {
      setOrganisations([]);
    }
  }

  async function loadApplications() {
    try {
      const data = await tryFetchJson(["/api/applications", "/api/aanvragen"]);
      setApplications(Array.isArray(data) ? data : []);
    } catch {
      setApplications([]);
    }
  }

  async function loadVacancies() {
    try {
      const data = await tryFetchJson(["/api/vacancies", "/api/vacatures"]);
      setVacancies(Array.isArray(data) ? data : []);
    } catch {
      setVacancies([]);
    }
  }

  async function loadPersonnel() {
    try {
      const data = await tryFetchJson(["/api/personnel"]);
      setPersonnel(Array.isArray(data) ? data : []);
    } catch {
      setPersonnel([]);
    }
  }

  async function loadInvoiceSettings() {
    try {
      const data = await tryFetchJson(["/api/invoice-settings"]);
      setInvoiceSettings(data && typeof data === "object" ? data : {});
    } catch {
      setInvoiceSettings({});
    }
  }

  async function loadContacts(organisationId) {
    const id = safeTrim(organisationId);
    if (!id) {
      setContacts([]);
      return;
    }

    try {
      const data = await tryFetchJson([`/api/contacts?organisation_id=${encodeURIComponent(id)}`]);
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    }
  }

  async function loadList(keepSelection = true) {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (safeTrim(q)) params.set("q", safeTrim(q));
      const data = await tryFetchJson([`/api/placements?${params.toString()}`, `/api/plaatsingen?${params.toString()}`]);
      const rows = Array.isArray(data) ? data.map(normalizePlacement) : [];

      setItems(rows);

      if (keepSelection && selectedId) {
        const idx = rows.findIndex((x) => String(x.id) === String(selectedId));
        if (idx >= 0) {
          setListIndex(idx);
          return rows;
        }
      }

      setListIndex(rows.length ? 0 : -1);
      return rows;
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
      setListIndex(-1);
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrganisations();
    loadApplications();
    loadVacancies();
    loadPersonnel();
    loadInvoiceSettings();
    loadList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadContacts(form.organisation_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.organisation_id]);

  function relationName(item) {
    return orgNameFromList(organisations, item?.organisation_id, item?.organisation_name || "");
  }

  function personName(item) {
    const wanted = String(item?.personnel_id || "");
    const found = (personnel || []).find((p) => String(p.id) === wanted);
    return personnelName(found) || item?.personnel_name || "";
  }

  function vacancyLabel(v) {
    const nr = safeTrim(v?.vacancy_number || "");
    const rel = orgNameFromList(organisations, v?.organisation_id, v?.organisation_name || "");
    const title = safeTrim(v?.title || v?.job_title || "");
    return [nr, rel, title].filter(Boolean).join(" - ") || `Vacature #${v?.id}`;
  }

  function applicationLabel(a) {
    const nr = safeTrim(a?.application_number || "");
    const rel = orgNameFromList(organisations, a?.organisation_id, a?.organisation_name || "");
    const title = safeTrim(a?.title || a?.job_title || "");
    return [nr, rel, title].filter(Boolean).join(" - ") || `Aanvraag #${a?.id}`;
  }

  function rowText(item) {
    return [
      item?.placement_date,
      item?.placement_number,
      relationName(item),
      personName(item),
      item?.job_title,
      item?.status,
      item?.start_date,
      item?.end_date,
      item?.hours_per_week,
      item?.location,
      item?.place,
      item?.region,
      item?.cao,
      item?.wage_period,
      item?.invoice_period,
      item?.factor,
      item?.hourly_wage,
      item?.sales_rate,
      item?.candidate_status,
      item?.customer_status,
      item?.internal_note,
      item?.external_note,
      item?.remarks,
      boolToJaNee(parseActiveValue(item) !== false),
    ].join(" ");
  }

  function filterBySelect(value, filter) {
    if (!filter) return true;
    const v = safeTrim(value);
    if (filter === EMPTY_VAL) return !v;
    return norm(v) === norm(filter);
  }

  const filtered = useMemo(() => {
    const query = norm(q);

    return (items || []).filter((item) => {
      if (query && !norm(rowText(item)).includes(query)) return false;
      if (!filterBySelect(formatDateNL(item.placement_date), dateFilter)) return false;
      if (!filterBySelect(relationName(item), relationFilter)) return false;
      if (!filterBySelect(personName(item), personFilter)) return false;
      if (!filterBySelect(item.job_title, functionFilter)) return false;
      if (!filterBySelect(item.status, statusFilter)) return false;
      if (!filterBySelect(item.cao, caoFilter)) return false;
      if (!filterBySelect(boolToJaNee(parseActiveValue(item) !== false), activeFilter)) return false;
      return true;
    });
  }, [items, q, dateFilter, relationFilter, personFilter, functionFilter, statusFilter, caoFilter, activeFilter, organisations, personnel]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "desc" ? -1 : 1;

    arr.sort((a, b) => {
      let av = "";
      let bv = "";

      if (sort.key === "date") {
        av = a.placement_date || "";
        bv = b.placement_date || "";
      } else if (sort.key === "relation") {
        av = relationName(a);
        bv = relationName(b);
      } else if (sort.key === "person") {
        av = personName(a);
        bv = personName(b);
      } else if (sort.key === "function") {
        av = a.job_title || "";
        bv = b.job_title || "";
      } else if (sort.key === "status") {
        av = a.status || "";
        bv = b.status || "";
      } else if (sort.key === "cao") {
        av = a.cao || "";
        bv = b.cao || "";
      }

      return String(av).localeCompare(String(bv), "nl") * dir;
    });

    return arr;
  }, [filtered, sort, organisations, personnel]);

  const optionValues = useMemo(() => ({
    date: uniqSorted(items.map((x) => formatDateNL(x.placement_date))),
    relation: uniqSorted(items.map((x) => relationName(x))),
    person: uniqSorted(items.map((x) => personName(x))),
    function: uniqSorted(items.map((x) => x.job_title)),
    status: uniqSorted(items.map((x) => x.status)),
    cao: uniqSorted(items.map((x) => x.cao)),
  }), [items, organisations, personnel]);

  const gridCount = Math.min(sorted.length, 9999);

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  function setDecimalField(name, value) {
    setForm((f) => ({ ...f, [name]: cleanDecimalInputValue(value) }));
  }

  function blurDecimalField(name, decimals = 2) {
    setForm((f) => ({ ...f, [name]: formatDecimalInputValue(f[name], decimals) }));
  }

  function newItem() {
    setSelectedId(null);
    setForm(emptyFormToday());
    setSaveError(null);
    setMode("form");
    setTimeout(() => dateRef.current?.focus?.(), 80);
  }

  function selectRow(item, idx) {
    setSelectedId(item.id);
    setForm(normalizePlacement(item));
    setListIndex(idx);
    setSaveError(null);
    setMode("form");
    setTimeout(() => {
      try { rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" }); } catch {}
    }, 50);
  }

  function openCurrentRow() {
    const item = sorted[listIndex];
    if (item) selectRow(item, listIndex);
  }

  function moveCursor(dir) {
    if (!sorted.length) return;
    const next = Math.max(0, Math.min(sorted.length - 1, (listIndex < 0 ? 0 : listIndex) + dir));
    setListIndex(next);
    const item = sorted[next];
    if (item) {
      setSelectedId(item.id);
      setForm(normalizePlacement(item));
    }
    setTimeout(() => rowRefs.current[next]?.scrollIntoView?.({ block: "nearest" }), 0);
  }

  async function saveItem() {
    if (isReadonlyRole) return;
    setSaveError(null);

    const payload = {
      ...form,
      hours_per_week: parseDecimal(form.hours_per_week),
      factor: parseDecimal(form.factor),
      hourly_wage: parseDecimal(form.hourly_wage),
      sales_rate: parseDecimal(form.sales_rate),
      travel_costs: parseDecimal(form.travel_costs),
      customer_confirmed: form.customer_confirmed === true,
      candidate_confirmed: form.candidate_confirmed === true,
      active: form.active !== false,
    };

    try {
      const saved = form.id
        ? await tryFetchJson([`/api/placements/${form.id}`, `/api/plaatsingen/${form.id}`], {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
        : await tryFetchJson(["/api/placements", "/api/plaatsingen"], {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

      const normalized = normalizePlacement(saved);
      setForm(normalized);
      setSelectedId(normalized.id);
      setFlash("Opgeslagen");
      setTimeout(() => setFlash(""), 1600);
      await loadList(true);
      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 80);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonlyRole || !form.id) return;
    if (!window.confirm("Plaatsing verwijderen?")) return;

    try {
      await tryFetchJson([`/api/placements/${form.id}`, `/api/plaatsingen/${form.id}`], { method: "DELETE" });
      setSelectedId(null);
      setForm(emptyFormToday());
      await loadList(false);
      setMode("search");
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function vernieuwList() {
    await loadList(true);
    setMode("search");
    setTimeout(() => searchRef.current?.focus?.(), 80);
  }

  function applyVacancy(vacancyId) {
    const v = (vacancies || []).find((x) => String(x.id) === String(vacancyId));
    if (!v) return;

    setForm((f) => ({
      ...f,
      vacancy_id: v.id || "",
      application_id: v.application_id || f.application_id || "",
      organisation_id: v.organisation_id || f.organisation_id || "",
      contact_id: v.contact_id || f.contact_id || "",
      job_title: v.job_title || f.job_title || "",
      start_date: isoDate(v.start_date) || f.start_date || "",
      end_date: isoDate(v.end_date) || f.end_date || "",
      hours_per_week: decimalInput(v.hours_per_week, 2) || f.hours_per_week || "",
      schedule_text: v.schedule_text || f.schedule_text || "",
      location: v.location || f.location || "",
      place: v.place || f.place || "",
      region: v.region || f.region || "",
      cao: v.cao || f.cao || "",
      wage_period: v.wage_period || f.wage_period || "week",
      invoice_period: v.invoice_period || f.invoice_period || "week",
      factor: decimalInput(v.factor, 2) || f.factor || "",
      sales_rate: decimalInput(v.sales_rate, 2) || f.sales_rate || "",
      travel_costs: decimalInput(v.travel_costs, 2) || f.travel_costs || "",
      allowances: v.allowances || f.allowances || "",
      invoice_agreement: v.invoice_agreement || f.invoice_agreement || "",
      internal_note: v.internal_note || f.internal_note || "",
      external_note: v.external_note || f.external_note || "",
      remarks: v.remarks || f.remarks || "",
    }));
  }


  const selectedOrganisation = useMemo(() => {
    const oid = String(form.organisation_id || "");
    if (!oid) return null;
    return (organisations || []).find((o) => String(orgIdOf(o)) === oid) || null;
  }, [form.organisation_id, organisations]);

  const selectedContact = useMemo(() => {
    const cid = String(form.contact_id || "");
    if (!cid) return null;
    return (contacts || []).find((c) => String(contactIdOf(c)) === cid) || null;
  }, [form.contact_id, contacts]);

  const selectedPersonnel = useMemo(() => {
    const pid = String(form.personnel_id || "");
    if (!pid) return null;
    return (personnel || []).find((p) => String(p.id || "") === pid) || null;
  }, [form.personnel_id, personnel]);

  function htmlEscape(v) {
    return String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function htmlLines(v) {
    const s = safeTrim(v);
    if (!s) return "-";
    return htmlEscape(s).replace(/\n/g, "<br/>");
  }

  function currentCustomerEmail() {
    return safeTrim(selectedContact?.email || selectedOrganisation?.email || "");
  }

  function currentCandidateEmail() {
    return safeTrim(selectedPersonnel?.email || "");
  }

  function buildPlacementPrintHtml(kind = "customer") {
    const isCandidate = kind === "candidate";
    const relation = pickOrgName(selectedOrganisation) || relationName(form);
    const contact = pickContactName(selectedContact);
    const candidate = personnelName(selectedPersonnel) || personName(form);
    const placementNo = safeTrim(form.placement_number || "") || String(form.id || "");
    const title = isCandidate ? "Plaatsingsbevestiging kandidaat" : "Plaatsingsbevestiging klant";
    const today = formatDateNL(new Date().toISOString().slice(0, 10));

    const companyName = safeTrim(invoiceSettings.offer_company_name || invoiceSettings.company_name || "StaffIQ");
    const companyAddress1 = safeTrim(invoiceSettings.offer_company_address_1 || invoiceSettings.company_address_1 || "");
    const companyAddress2 = safeTrim(invoiceSettings.offer_company_address_2 || invoiceSettings.company_address_2 || "");
    const companyPostcodeCity = [
      safeTrim(invoiceSettings.offer_company_postcode || invoiceSettings.company_postcode || ""),
      safeTrim(invoiceSettings.offer_company_city || invoiceSettings.company_city || ""),
    ].filter(Boolean).join(" ");
    const companyEmail = safeTrim(invoiceSettings.offer_company_email || invoiceSettings.company_email || "");
    const companyPhone = safeTrim(invoiceSettings.offer_company_phone || invoiceSettings.company_phone || "");
    const companyWebsite = safeTrim(invoiceSettings.offer_company_website || invoiceSettings.company_website || "");
    const logoUrl = safeTrim(invoiceSettings.logo_url || "");

    const customerIntro = `Hierbij bevestigen wij de plaatsing van ${candidate || "de kandidaat"} bij ${relation || "de opdrachtgever"}.`;
    const candidateIntro = `Hierbij bevestigen wij jouw plaatsing bij ${relation || "de opdrachtgever"}.`;

    const row = (label, value) => {
      const text = safeTrim(value);
      if (!text) return "";
      return `<tr><td>${htmlEscape(label)}</td><td>${htmlLines(text)}</td></tr>`;
    };

    const logoBlock = logoUrl
      ? `<img src="${htmlEscape(logoUrl)}" alt="${htmlEscape(companyName)}" style="max-width:190px; max-height:80px; object-fit:contain;" />`
      : `<div style="font-size:28px; font-weight:700; color:#0b4f7a;">${htmlEscape(companyName)}</div>`;

    const companyLines = [companyAddress1, companyAddress2, companyPostcodeCity, companyEmail, companyPhone, companyWebsite]
      .filter(Boolean)
      .map((x) => htmlEscape(x))
      .join("<br/>");

    const recipientBlock = isCandidate
      ? `
        <div class="label">Kandidaat</div>
        <div class="recipient-name">${htmlLines(candidate)}</div>
      `
      : `
        <div class="label">Relatie</div>
        <div class="recipient-name">${htmlLines(relation)}</div>
        ${contact ? `<div>t.a.v. ${htmlLines(contact)}</div>` : ""}
      `;

    const moneySuffix = isCandidate ? "" : " excl. btw";

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${htmlEscape(title)} ${htmlEscape(placementNo)}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              font-family: Arial, Helvetica, sans-serif;
              color: #222;
              font-size: 12px;
              line-height: 1.45;
            }
            .wrap { width: 180mm; max-width: 180mm; margin: 0 auto; }
            .header { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; align-items: start; margin-bottom: 26px; }
            .company { text-align: right; color: #444; font-size: 11px; }
            .doc-title { background: #0b4f7a; color: #fff; padding: 10px 12px; font-size: 19px; font-weight: 700; margin: 0 0 18px 0; }
            .topgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 20px; }
            .label { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px; }
            .recipient-name { font-weight: 700; font-size: 13px; }
            h2 { font-size: 14px; margin: 22px 0 8px 0; color: #0b4f7a; border-bottom: 1px solid #d9d9d9; padding-bottom: 4px; }
            p { margin: 0 0 12px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            td { padding: 6px 8px; border-bottom: 1px solid #ededed; vertical-align: top; }
            td:first-child { width: 38%; font-weight: 700; color: #444; background: #fafafa; }
            .notice { margin-top: 18px; padding: 10px 12px; background: #f6f8fa; border-left: 4px solid #0b4f7a; }
            .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #d9d9d9; color: #666; font-size: 11px; }
            button { display: none !important; }
          </style>
        </head>
        <body>
          <div class="wrap">
            <div class="header">
              <div>${logoBlock}</div>
              <div class="company">${companyLines}</div>
            </div>

            <div class="doc-title">${htmlEscape(title)}</div>

            <div class="topgrid">
              <div>${recipientBlock}</div>
              <div>
                <div class="label">Datum</div>
                <div>${htmlLines(today)}</div>
                <br/>
                <div class="label">Plaatsing</div>
                <div>${htmlLines(placementNo)}</div>
              </div>
            </div>

            <p>${htmlEscape(isCandidate ? candidateIntro : customerIntro)}</p>

            <h2>Plaatsingsgegevens</h2>
            <table>
              <tbody>
                ${row("Functie", form.job_title)}
                ${row("Kandidaat", candidate)}
                ${isCandidate ? row("Opdrachtgever", relation) : row("Relatie", relation)}
                ${!isCandidate ? row("Contactpersoon", contact) : row("Contactpersoon opdrachtgever", contact)}
                ${row("Startdatum", formatDateNL(form.start_date))}
                ${row("Einddatum", formatDateNL(form.end_date))}
                ${row("Uren per week", form.hours_per_week)}
                ${row("Werktijden / rooster", form.schedule_text)}
                ${row("Locatie", form.location)}
                ${row("Plaats", form.place)}
                ${row("Regio", form.region)}
              </tbody>
            </table>

            <h2>${isCandidate ? "Arbeidsafspraken" : "Tarief- en factuurafspraken"}</h2>
            <table>
              <tbody>
                ${row("CAO", form.cao)}
                ${isCandidate ? row("Loonperiode", form.wage_period) : ""}
                ${isCandidate ? row("Uurloon", form.hourly_wage) : ""}
                ${!isCandidate ? row("Factuurperiode", form.invoice_period) : ""}
                ${!isCandidate ? row("Verkooptarief", form.sales_rate ? `${form.sales_rate}${moneySuffix}` : "") : ""}
                ${!isCandidate ? row("Factor", form.factor) : ""}
                ${row("Reiskosten", form.travel_costs)}
                ${row("Toeslagen", form.allowances)}
                ${!isCandidate ? row("Factuurafspraak", form.invoice_agreement) : ""}
              </tbody>
            </table>

            ${form.external_note ? `
              <h2>Opmerking</h2>
              <div class="notice">${htmlLines(form.external_note)}</div>
            ` : ""}

            <p style="margin-top:24px;">Wij verzoeken u deze gegevens te controleren en eventuele afwijkingen zo spoedig mogelijk aan ons door te geven.</p>

            <div class="footer">
              ${htmlEscape(companyName || "StaffIQ")} · automatisch aangemaakt vanuit StaffIQ
            </div>
          </div>
        </body>
      </html>
    `;
  }

  function previewPlacementConfirmation(kind) {
    if (!form.id) {
      setSaveError("Sla de plaatsing eerst op voordat je een bevestiging maakt.");
      return;
    }

    const html = buildPlacementPrintHtml(kind);
    const w = window.open("", "_blank", "width=1000,height=800");

    if (!w) {
      setSaveError("Popup kon niet worden geopend.");
      return;
    }

    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
  }

  async function savePlacementConfirmation(kind) {
    if (isReadonlyRole) return;

    if (!form.id) {
      setSaveError("Sla de plaatsing eerst op voordat je een bevestiging opslaat.");
      return;
    }

    setSaveError(null);

    try {
      const html = buildPlacementPrintHtml(kind);
      const result = await tryFetchJson([`/api/placements/${encodeURIComponent(form.id)}/save-document-html`, `/api/plaatsingen/${encodeURIComponent(form.id)}/save-document-html`], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html, kind }),
      });

      const docId = result?.document?.id ? ` (#${result.document.id})` : "";
      const actionText = result?.action === "updated" ? "bijgewerkt" : "opgeslagen";
      setFlash(kind === "candidate"
        ? `Bevestiging kandidaat ${actionText} bij documenten${docId} ✅`
        : `Bevestiging klant ${actionText} bij documenten${docId} ✅`
      );
      setTimeout(() => setFlash(""), 2200);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function mailPlacementConfirmation(kind) {
    if (isReadonlyRole) return;

    if (!form.id) {
      setSaveError("Sla de plaatsing eerst op voordat je een bevestiging mailt.");
      return;
    }

    // Het scherm probeert het e-mailadres alvast te bepalen, maar de backend zoekt
    // voor de zekerheid ook zelf nog in plaatsing + contactpersoon + relatie + kandidaat.
    const to = kind === "candidate" ? currentCandidateEmail() : currentCustomerEmail();

    setSaveError(null);

    try {
      const html = buildPlacementPrintHtml(kind);
      const result = await tryFetchJson([`/api/placements/${encodeURIComponent(form.id)}/send-email`, `/api/plaatsingen/${encodeURIComponent(form.id)}/send-email`], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to,
          kind,
          subject: `Plaatsingsbevestiging ${form.placement_number || form.id}`,
          html,
        }),
      });

      const sentTo = result?.to || to || "ontvanger";
      setFlash(`Bevestiging verstuurd naar ${sentTo} ✅`);
      setTimeout(() => setFlash(""), 2200);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function getFocusableElements() {
    return focusOrderRefs
      .map((r) => r.current)
      .filter((el) => el && !el.disabled && !el.readOnly);
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;
    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : 0;
    const next = els[Math.max(0, Math.min(els.length - 1, idx + dir))];
    try {
      next?.focus?.();
      next?.select?.();
    } catch {}
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;
      if (cmd.type === "next") return mode === "form" ? focusNextField(+1) : moveCursor(+1), true;
      if (cmd.type === "prev") return mode === "form" ? focusNextField(-1) : moveCursor(-1), true;
      if (cmd.type === "new") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;
      if (cmd.type === "select") return openCurrentRow(), true;
      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;
      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);
      const t = norm(raw);
      if (t === "volgende") return mode === "form" ? focusNextField(+1) : moveCursor(+1);
      if (t === "vorige" || t === "terug") return mode === "form" ? focusNextField(-1) : moveCursor(-1);
      if (t === "nieuw") return newItem();
      if (t === "vernieuw" || t === "vernieuwen") return vernieuwList();
      if (t === "opslaan") return saveItem();
      if (t === "selecteer" || t === "item" || t === "rechts") return openCurrentRow();
      if (t === "lijst" || t === "links") return setMode("search");
      if (mode === "search") {
        if (isEmptyWord(raw)) setQ("");
        else setQ(raw);
      }
    },
  });

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1120;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

  const colWidths = {
    date: 90,
    relation: 160,
    person: 160,
    function: 160,
    status: 100,
    cao: 80,
    active: 70,
  };

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    color: "#333",
    marginBottom: 3,
  };

  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.75,
    marginTop: 8,
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: "#2f7dd1",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    background: "#d64545",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const successButtonStyle = {
    ...buttonStyle,
    background: "#198754",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const neutralButtonStyle = {
    ...buttonStyle,
    background: "#6c757d",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const warningButtonStyle = {
    ...buttonStyle,
    background: "#fd7e14",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  // Velden blijven wit, net als bij de andere programma's.
  const disabledStyle = null;

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";

  function sortToggle(key) {
    setSort((cur) => {
      if (cur.key !== key) return { key, dir: "asc" };
      return { key, dir: cur.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function SortHeader({ label, k }) {
    return (
      <th style={thClickable} onClick={() => sortToggle(k)}>
        {label}{sortArrow(k)}
      </th>
    );
  }

  function FilterSelect({ value, onChange, options, includeYesNo = false }) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={stopGridClick}
        style={selectStyle}
      >
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          <>
            <option value={EMPTY_VAL}>Leeg</option>
            {(options || []).map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </>
        )}
      </select>
    );
  }

  function todayIso() {
    return new Date().toISOString().slice(0, 10);
  }

  function processButtonStyle(baseStyle, disabled = false) {
    return {
      ...(disabled ? { ...baseStyle, opacity: 0.5, cursor: "not-allowed" } : baseStyle),
      width: "100%",
      minWidth: 0,
      height: 32,
      padding: "7px 8px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      whiteSpace: "nowrap",
    };
  }

  async function savePlacementProcessFields(fields, message, confirmText) {
    if (isReadonlyRole) return;

    if (!form.id) {
      setSaveError("Sla de plaatsing eerst op voordat je de status wijzigt.");
      return;
    }

    if (confirmText && !window.confirm(confirmText)) return;

    setSaveError(null);

    const nextForm = {
      ...form,
      ...fields,
    };

    const payload = {
      ...nextForm,
      hours_per_week: parseDecimal(nextForm.hours_per_week),
      factor: parseDecimal(nextForm.factor),
      hourly_wage: parseDecimal(nextForm.hourly_wage),
      sales_rate: parseDecimal(nextForm.sales_rate),
      travel_costs: parseDecimal(nextForm.travel_costs),
      customer_confirmed: nextForm.customer_confirmed === true,
      candidate_confirmed: nextForm.candidate_confirmed === true,
      active: nextForm.active !== false,
    };

    try {
      const saved = await tryFetchJson(
        [`/api/placements/${nextForm.id}`, `/api/plaatsingen/${nextForm.id}`],
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const normalized = normalizePlacement(saved);
      setForm(normalized);
      setSelectedId(normalized.id);
      setMode("form");
      setFlash(`${message} ✅`);
      setTimeout(() => setFlash(""), 1800);
      await loadList(true);
      setTimeout(() => {
        try { rightScrollRef.current?.scrollTo?.({ top: rightScrollRef.current?.scrollTop || 0, behavior: "auto" }); } catch {}
      }, 80);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function markProposed() {
    savePlacementProcessFields(
      {
        status: "Voorgesteld",
        candidate_status: "Voorgesteld",
        customer_status: "In behandeling",
      },
      "Plaatsing voorgesteld",
      "Plaatsing op Voorgesteld zetten?"
    );
  }

  function markCandidateConfirmed() {
    savePlacementProcessFields(
      {
        candidate_confirmed: true,
        candidate_status: "Akkoord",
      },
      "Kandidaat akkoord",
      "Kandidaat akkoord zetten?"
    );
  }

  function markCustomerConfirmed() {
    savePlacementProcessFields(
      {
        customer_confirmed: true,
        customer_status: "Akkoord",
      },
      "Klant akkoord",
      "Klant akkoord zetten?"
    );
  }

  function markConfirmed() {
    if (form.candidate_confirmed !== true || form.customer_confirmed !== true) {
      alert("Bevestigen kan pas als kandidaat én klant akkoord zijn.");
      return;
    }

    savePlacementProcessFields(
      {
        status: "Bevestigd",
        candidate_status: "Akkoord",
        customer_status: "Akkoord",
      },
      "Plaatsing bevestigd. Bevestigingen kunnen nu worden aangemaakt",
      "Plaatsing bevestigen?"
    );
  }

  function markStarted() {
    savePlacementProcessFields(
      {
        status: "Gestart",
        candidate_status: "Gestart",
        customer_status: "Gestart",
      },
      "Plaatsing gestart",
      "Plaatsing starten?"
    );
  }

  function markStopped() {
    savePlacementProcessFields(
      {
        status: "Gestopt",
        end_date: form.end_date || todayIso(),
        candidate_status: "Gestopt",
        customer_status: "Gestopt",
      },
      "Plaatsing gestopt",
      "Plaatsing stoppen?"
    );
  }

  function markCancelled() {
    savePlacementProcessFields(
      {
        status: "Geannuleerd",
        candidate_status: "Geannuleerd",
        customer_status: "Geannuleerd",
      },
      "Plaatsing geannuleerd",
      "Plaatsing annuleren?"
    );
  }

  const currentPlacementStatusNorm = norm(form.status || "Concept");
  const hasSavedPlacement = !!form.id;
  const statusActionDisabled = isReadonlyRole || mode !== "form" || !hasSavedPlacement;
  const isConceptStatus = currentPlacementStatusNorm === "concept" || currentPlacementStatusNorm === "";
  const isProposedStatus = currentPlacementStatusNorm === "voorgesteld";
  const isConfirmedStatus = currentPlacementStatusNorm === "bevestigd";
  const isStartedStatus = currentPlacementStatusNorm === "gestart";
  const isStoppedStatus = currentPlacementStatusNorm === "gestopt";
  const isCancelledStatus = currentPlacementStatusNorm === "geannuleerd";
  const hasBothConfirmed = form.candidate_confirmed === true && form.customer_confirmed === true;

  const showProposeButton = isConceptStatus;

  const showCandidateOkButton =
    isProposedStatus && form.candidate_confirmed !== true;

  const showCustomerOkButton =
    isProposedStatus && form.customer_confirmed !== true;

  const showConfirmButton =
    isProposedStatus && hasBothConfirmed;

  const showStartButton = isConfirmedStatus;
  const showStopButton = isStartedStatus;

  const showCancelButton =
    isConceptStatus || isProposedStatus || isConfirmedStatus;

  const showDocumentSection =
    hasSavedPlacement && (isConfirmedStatus || isStartedStatus || isStoppedStatus);

  const showProcessSection =
    !isStoppedStatus && !isCancelledStatus && (
      showProposeButton ||
      showCandidateOkButton ||
      showCustomerOkButton ||
      showConfirmButton ||
      showStartButton ||
      showStopButton ||
      showCancelButton
    );

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 ref={pageTopRef} style={{ marginTop: 0, marginBottom: 10 }}>
            Plaatsingen <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>
          {!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setMode("search");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadList(false);
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => (voice.startOnce || voice.start)?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button
                type="button"
                onClick={newItem}
                disabled={isReadonlyRole}
                style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}
              >
                ➕ Nieuw
              </button>

              <button type="button" onClick={vernieuwList} style={buttonStyle}>
                🔄 Vernieuw
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") loadList(false);
                  }}
                  placeholder="Zoek..."
                  style={{ ...searchInputStyle, width: "100%", minWidth: 0 }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => (voice.startOnce || voice.start)?.()}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>

                <button
                  type="button"
                  onClick={newItem}
                  disabled={isReadonlyRole}
                  style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}
                >
                  ➕ Nieuw
                </button>

                <button type="button" onClick={vernieuwList} style={buttonStyle}>
                  🔄 Vernieuw
                </button>
              </div>
            </>
          )}

          <div style={{ marginTop: 10 }}>
            {loading && <div>Laden...</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {speechInfo && <div style={{ opacity: 0.75 }}>{speechInfo}</div>}
            {voice.error && voice.error !== "no-speech" && (
              <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>
            )}
          </div>

          <div style={{ marginTop: 8, minWidth: 0 }}>
            <div
              style={{
                overflowX: "scroll",
                overflowY: "auto",
                maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                background: "#fff",
              }}
            >
              <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: colWidths.date }} />
                    <col style={{ width: colWidths.relation }} />
                    <col style={{ width: colWidths.person }} />
                    <col style={{ width: colWidths.function }} />
                    <col style={{ width: colWidths.status }} />
                    <col style={{ width: colWidths.cao }} />
                    <col style={{ width: colWidths.active }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <SortHeader label="Datum" k="date" />
                      <SortHeader label="Relatie" k="relation" />
                      <SortHeader label="Personeelslid" k="person" />
                      <SortHeader label="Functie" k="function" />
                      <SortHeader label="Status" k="status" />
                      <SortHeader label="CAO" k="cao" />
                      <th style={thClickable}>Actief</th>
                    </tr>

                    <tr>
                      <th style={filterThStyle}><FilterSelect value={dateFilter} onChange={setDateFilter} options={optionValues.date} /></th>
                      <th style={filterThStyle}><FilterSelect value={relationFilter} onChange={setRelationFilter} options={optionValues.relation} /></th>
                      <th style={filterThStyle}><FilterSelect value={personFilter} onChange={setPersonFilter} options={optionValues.person} /></th>
                      <th style={filterThStyle}><FilterSelect value={functionFilter} onChange={setFunctionFilter} options={optionValues.function} /></th>
                      <th style={filterThStyle}><FilterSelect value={statusFilter} onChange={setStatusFilter} options={optionValues.status} /></th>
                      <th style={filterThStyle}><FilterSelect value={caoFilter} onChange={setCaoFilter} options={optionValues.cao} /></th>
                      <th style={filterThStyle}><FilterSelect value={activeFilter} onChange={setActiveFilter} options={[]} includeYesNo /></th>
                    </tr>
                  </thead>

                  <tbody>
                    {sorted.map((item, idx) => {
                      const isCursor = idx === listIndex;
                      const isSelected = String(selectedId || "") === String(item.id || "");

                      let bg = "transparent";
                      if (isSelected) bg = rowBgSelected;
                      else if (isCursor) bg = rowBgCursor;

                      return (
                        <tr
                          key={item.id}
                          ref={(el) => { rowRefs.current[idx] = el; }}
                          onClick={() => selectRow(item, idx)}
                          style={{ cursor: "pointer", backgroundColor: bg }}
                        >
                          <td style={tdStyle}>{formatDateNL(item.placement_date) || "-"}</td>
                          <td style={tdStyle} title={relationName(item)}>{relationName(item) || "-"}</td>
                          <td style={tdStyle} title={personName(item)}>{personName(item) || "-"}</td>
                          <td style={tdStyle} title={item.job_title || ""}>{item.job_title || "-"}</td>
                          <td style={tdStyle}>{item.status || "-"}</td>
                          <td style={tdStyle}>{item.cao || "-"}</td>
                          <td style={tdStyle}>{boolToJaNee(item.active !== false)}</td>
                        </tr>
                      );
                    })}

                    {sorted.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 12, opacity: 0.7 }}>
                          Geen plaatsingen gevonden.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            paddingBottom: isMobile ? 34 : 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            marginBottom: isMobile ? 34 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 12,
              paddingBottom: isMobile ? 28 : 12,
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isMobile ? "visible" : "hidden",
            }}
          >
          <div style={{ marginBottom: 10 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>
              Plaatsing
            </h3>
          </div>

          {saveError && <div style={{ color: "#b00020", marginBottom: 8 }}>{saveError}</div>}
          {flash && <div style={{ color: "#1f7a1f", marginBottom: 8 }}>{flash}</div>}

          <Field label="Plaatsingsdatum">
            <input ref={dateRef} type="date" value={form.placement_date || ""} onChange={(e) => setField("placement_date", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Plaatsingsnummer">
            <input ref={numberRef} type="text" autoComplete="off" value={form.placement_number || ""} onChange={(e) => setField("placement_number", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Status">
            <select ref={statusRef} value={form.status || ""} onChange={(e) => setField("status", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option>Concept</option>
              <option>Voorgesteld</option>
              <option>Bevestigd</option>
              <option>Gestart</option>
              <option>Gestopt</option>
              <option>Geannuleerd</option>
            </select>
          </Field>

          <Field label="Aanvraag">
            <select ref={applicationRef} value={form.application_id || ""} onChange={(e) => setField("application_id", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="">-</option>
              {applications.map((a) => <option key={a.id} value={a.id}>{applicationLabel(a)}</option>)}
            </select>
          </Field>

          <Field label="Vacature">
            <select ref={vacancyRef} value={form.vacancy_id || ""} onChange={(e) => { setField("vacancy_id", e.target.value); applyVacancy(e.target.value); }} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="">-</option>
              {vacancies.map((v) => <option key={v.id} value={v.id}>{vacancyLabel(v)}</option>)}
            </select>
          </Field>

          <Field label="Relatie">
            <select ref={relationRef} value={form.organisation_id || ""} onChange={(e) => setField("organisation_id", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="">-</option>
              {organisations.map((o) => <option key={orgIdOf(o)} value={orgIdOf(o)}>{pickOrgName(o)}</option>)}
            </select>
          </Field>

          <Field label="Contactpersoon">
            <select ref={contactRef} value={form.contact_id || ""} onChange={(e) => setField("contact_id", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="">-</option>
              {contacts.map((c) => <option key={contactIdOf(c)} value={contactIdOf(c)}>{pickContactName(c)}</option>)}
            </select>
          </Field>

          <Field label="Personeelslid">
            <select ref={personnelRef} value={form.personnel_id || ""} onChange={(e) => setField("personnel_id", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="">-</option>
              {personnel.map((p) => <option key={p.id} value={p.id}>{personnelName(p)}</option>)}
            </select>
          </Field>

          <Field label="Functie">
            <input ref={functionRef} value={form.job_title || ""} onChange={(e) => setField("job_title", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <div style={{ borderTop: "1px solid #e5e5e5", margin: "12px 0", paddingTop: 10 }}>
            <div style={sectionTitleStyle}>Periode / inzet</div>
          </div>

          <Field label="Startdatum">
            <input ref={startDateRef} type="date" value={form.start_date || ""} onChange={(e) => setField("start_date", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Einddatum">
            <input ref={endDateRef} type="date" value={form.end_date || ""} onChange={(e) => setField("end_date", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Uren per week">
            <input ref={hoursRef} inputMode="decimal" value={form.hours_per_week || ""} onChange={(e) => setDecimalField("hours_per_week", e.target.value)} onBlur={() => blurDecimalField("hours_per_week", 2)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Werktijden / rooster">
            <textarea ref={scheduleRef} value={form.schedule_text || ""} onChange={(e) => setField("schedule_text", e.target.value)} disabled={isFormLocked} rows={2} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Locatie">
            <input ref={locationRef} value={form.location || ""} onChange={(e) => setField("location", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Plaats">
            <input ref={placeRef} value={form.place || ""} onChange={(e) => setField("place", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Regio">
            <input ref={regionRef} value={form.region || ""} onChange={(e) => setField("region", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <div style={{ borderTop: "1px solid #e5e5e5", margin: "12px 0", paddingTop: 10 }}>
            <div style={sectionTitleStyle}>Tarieven / afspraken</div>
          </div>

          <Field label="CAO">
            <input ref={caoRef} value={form.cao || ""} onChange={(e) => setField("cao", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Loonperiode">
            <select ref={wagePeriodRef} value={form.wage_period || ""} onChange={(e) => setField("wage_period", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="week">week</option>
              <option value="4-weken">4-weken</option>
              <option value="maand">maand</option>
            </select>
          </Field>

          <Field label="Factuurperiode">
            <select ref={invoicePeriodRef} value={form.invoice_period || ""} onChange={(e) => setField("invoice_period", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }}>
              <option value="week">week</option>
              <option value="4-weken">4-weken</option>
              <option value="maand">maand</option>
            </select>
          </Field>

          <Field label="Factor">
            <input ref={factorRef} inputMode="decimal" value={form.factor || ""} onChange={(e) => setDecimalField("factor", e.target.value)} onBlur={() => blurDecimalField("factor", 2)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Uurloon">
            <input ref={wageRef} inputMode="decimal" value={form.hourly_wage || ""} onChange={(e) => setDecimalField("hourly_wage", e.target.value)} onBlur={() => blurDecimalField("hourly_wage", 2)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Verkooptarief">
            <input ref={salesRateRef} inputMode="decimal" value={form.sales_rate || ""} onChange={(e) => setDecimalField("sales_rate", e.target.value)} onBlur={() => blurDecimalField("sales_rate", 2)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Reiskosten">
            <input ref={travelCostsRef} inputMode="decimal" value={form.travel_costs || ""} onChange={(e) => setDecimalField("travel_costs", e.target.value)} onBlur={() => blurDecimalField("travel_costs", 2)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Toeslagen">
            <textarea ref={allowancesRef} value={form.allowances || ""} onChange={(e) => setField("allowances", e.target.value)} disabled={isFormLocked} rows={2} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Factuurafspraak">
            <textarea ref={invoiceAgreementRef} value={form.invoice_agreement || ""} onChange={(e) => setField("invoice_agreement", e.target.value)} disabled={isFormLocked} rows={2} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <div style={{ borderTop: "1px solid #e5e5e5", margin: "12px 0", paddingTop: 10 }}>
            <div style={sectionTitleStyle}>Proces</div>
          </div>

          <Field label="Status kandidaat">
            <input ref={candidateStatusRef} value={form.candidate_status || ""} onChange={(e) => setField("candidate_status", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Status klant">
            <input ref={customerStatusRef} value={form.customer_status || ""} onChange={(e) => setField("customer_status", e.target.value)} disabled={isFormLocked} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input type="checkbox" checked={form.candidate_confirmed === true} onChange={(e) => setField("candidate_confirmed", e.target.checked)} disabled={isFormLocked} />
            Kandidaat bevestigd
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input type="checkbox" checked={form.customer_confirmed === true} onChange={(e) => setField("customer_confirmed", e.target.checked)} disabled={isFormLocked} />
            Klant bevestigd
          </label>

          <Field label="Opmerking intern">
            <textarea ref={internalNoteRef} value={form.internal_note || ""} onChange={(e) => setField("internal_note", e.target.value)} disabled={isFormLocked} rows={3} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <Field label="Opmerking extern">
            <textarea ref={externalNoteRef} value={form.external_note || ""} onChange={(e) => setField("external_note", e.target.value)} disabled={isFormLocked} rows={3} style={{ ...inputStyle, ...disabledStyle }} />
          </Field>

          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <input type="checkbox" checked={form.active !== false} onChange={(e) => setField("active", e.target.checked)} disabled={isFormLocked} />
            Actief
          </label>

          {showDocumentSection && (
            <>
          <div style={{ borderTop: "1px solid #e5e5e5", margin: "12px 0", paddingTop: 10 }}>
            <div style={sectionTitleStyle}>Documenten / bevestigingen</div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              width: "100%",
              boxSizing: "border-box",
              marginBottom: 8,
            }}
          >
            <button
              type="button"
              onClick={() => previewPlacementConfirmation("customer")}
              disabled={!form.id}
              style={processButtonStyle(neutralButtonStyle, !form.id)}
            >
              👁 Voorbeeld klant
            </button>

            <button
              type="button"
              onClick={() => previewPlacementConfirmation("candidate")}
              disabled={!form.id}
              style={processButtonStyle(neutralButtonStyle, !form.id)}
            >
              👁 Voorbeeld kandidaat
            </button>

            <button
              type="button"
              onClick={() => savePlacementConfirmation("customer")}
              disabled={isReadonlyRole || !form.id}
              style={processButtonStyle(successButtonStyle, isReadonlyRole || !form.id)}
            >
              💾 Opslaan klant
            </button>

            <button
              type="button"
              onClick={() => savePlacementConfirmation("candidate")}
              disabled={isReadonlyRole || !form.id}
              style={processButtonStyle(successButtonStyle, isReadonlyRole || !form.id)}
            >
              💾 Opslaan kandidaat
            </button>

            <button
              type="button"
              onClick={() => mailPlacementConfirmation("customer")}
              disabled={isReadonlyRole || !form.id}
              style={processButtonStyle(primaryButtonStyle, isReadonlyRole || !form.id)}
            >
              📧 Mail klant
            </button>

            <button
              type="button"
              onClick={() => mailPlacementConfirmation("candidate")}
              disabled={isReadonlyRole || !form.id}
              style={processButtonStyle(primaryButtonStyle, isReadonlyRole || !form.id)}
            >
              📧 Mail kandidaat
            </button>
          </div>

            </>
          )}

          <div
            style={{
              borderTop: "1px solid #e5e5e5",
              paddingTop: 10,
              marginTop: 8,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <div style={{ ...sectionTitleStyle, marginTop: 0, marginBottom: 8 }}>
              Status / opvolging
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                width: "100%",
                boxSizing: "border-box",
                marginBottom: 8,
              }}
            >
              {showProposeButton && (
                <button
                  type="button"
                  onClick={markProposed}
                  disabled={statusActionDisabled}
                  style={processButtonStyle(successButtonStyle, statusActionDisabled)}
                >
                  📤 Voorstellen
                </button>
              )}

              {showCandidateOkButton && (
                <button
                  type="button"
                  onClick={markCandidateConfirmed}
                  disabled={statusActionDisabled}
                  style={processButtonStyle(successButtonStyle, statusActionDisabled)}
                >
                  ✅ Kandidaat akkoord
                </button>
              )}

              {showCustomerOkButton && (
                <button
                  type="button"
                  onClick={markCustomerConfirmed}
                  disabled={statusActionDisabled}
                  style={processButtonStyle(successButtonStyle, statusActionDisabled)}
                >
                  ✅ Klant akkoord
                </button>
              )}

              {showConfirmButton && (
                <button
                  type="button"
                  onClick={markConfirmed}
                  disabled={
                    statusActionDisabled ||
                    form.candidate_confirmed !== true ||
                    form.customer_confirmed !== true
                  }
                  title={
                    form.candidate_confirmed === true && form.customer_confirmed === true
                      ? "Plaatsing bevestigen"
                      : "Eerst kandidaat én klant akkoord zetten"
                  }
                  style={processButtonStyle(
                    primaryButtonStyle,
                    statusActionDisabled ||
                      form.candidate_confirmed !== true ||
                      form.customer_confirmed !== true
                  )}
                >
                  🤝 Bevestigen
                </button>
              )}

              {showStartButton && (
                <button
                  type="button"
                  onClick={markStarted}
                  disabled={statusActionDisabled}
                  style={processButtonStyle(successButtonStyle, statusActionDisabled)}
                >
                  ▶️ Starten
                </button>
              )}

              {showStopButton && (
                <button
                  type="button"
                  onClick={markStopped}
                  disabled={statusActionDisabled}
                  style={processButtonStyle(warningButtonStyle, statusActionDisabled)}
                >
                  ⏹ Stoppen
                </button>
              )}

              {showCancelButton && (
                <button
                  type="button"
                  onClick={markCancelled}
                  disabled={statusActionDisabled}
                  style={processButtonStyle(neutralButtonStyle, statusActionDisabled)}
                >
                  🚫 Annuleren
                </button>
              )}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <button
                type="button"
                onClick={saveItem}
                disabled={isReadonlyRole || mode !== "form"}
                style={processButtonStyle(
                  primaryButtonStyle,
                  isReadonlyRole || mode !== "form"
                )}
              >
                💾 Opslaan
              </button>

              <button
                type="button"
                onClick={deleteItem}
                disabled={isReadonlyRole || !form.id}
                style={processButtonStyle(
                  dangerButtonStyle,
                  isReadonlyRole || !form.id
                )}
              >
                🗑 Verwijderen
              </button>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "#777" }}>
              Plaatsing-id: {form.id || "(nieuw)"}
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
